import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import Breadcrumb from '../../components/Breadcrumb';
import { useEffect, useState } from 'react';
import { useScrollReveal } from '../../hooks/useScrollReveal';

export default function OpenCellSprayFoamPage() {
  const [activeTab, setActiveTab] = useState('benefits');
  const statsReveal = useScrollReveal({ threshold: 0.3 });

  // SEO & Schema handling
  useEffect(() => {
    // Page title
    document.title = 'Open Cell Foam Yuma AZ | Affordable Insulation';

    // Meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        'content',
        'Affordable open cell spray foam insulation in Yuma, AZ. R-3.5 per inch, sound dampening, flexible. Residential & commercial. Call (928) 427-8140.'
      );
    }

    // Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', window.location.origin + '/open-cell-spray-foam-yuma');

    // Geo-location tags
    const metaTags = [
      { name: 'geo.region', content: 'US-AZ' },
      { name: 'geo.placename', content: 'Yuma' },
      { name: 'geo.position', content: '32.6927;-114.6277' },
      { name: 'ICBM', content: '32.6927, -114.6277' },
    ];
    metaTags.forEach(tag => {
      let el = document.querySelector(`meta[name="${tag.name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', tag.name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', tag.content);
    });

    // Open Graph tags
    const ogTags = [
      { property: 'og:title', content: 'Open Cell Foam Yuma AZ | Affordable Spray Foam Insulation' },
      {
        property: 'og:description',
        content: 'Affordable open cell spray foam insulation in Yuma, AZ. R-3.5 per inch, sound dampening, flexible.'
      },
      { property: 'og:type', content: 'website' },
      { property: 'og:url', content: window.location.origin + '/open-cell-spray-foam-yuma' },
      {
        property: 'og:image',
        content:
          'https://public.readdy.ai/ai/img_res/434eff8b406a5f8dcfde32eb849ad464.jpg'
      }
    ];
    ogTags.forEach(tag => {
      let el = document.querySelector(`meta[property="${tag.property}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('property', tag.property);
        document.head.appendChild(el);
      }
      el.setAttribute('content', tag.content);
    });

    // Twitter Card tags
    const twitterTags = [
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: 'Open Cell Foam Yuma AZ | Affordable Spray Foam Insulation' },
      {
        name: 'twitter:description',
        content: 'Affordable open cell spray foam insulation. R-3.5 per inch, sound dampening, flexible.'
      },
      {
        name: 'twitter:image',
        content:
          'https://readdy.ai/api/search-image?query=Professional%20open%20cell%20spray%20foam%20insulation%20application%20showing%20soft%20flexible%20foam%20texture%20with%20excellent%20coverage%20and%20even%20distribution%20highlighting%20cost-effective%20thermal%20and%20acoustic%20insulation%20properties&width=1200&height=630&seq=twitter-open-cell-001&orientation=landscape'
      }
    ];
    twitterTags.forEach(tag => {
      let el = document.querySelector(`meta[name="${tag.name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', tag.name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', tag.content);
    });

    // Last-modified tag
    let lastModified = document.querySelector('meta[name="last-modified"]');
    if (!lastModified) {
      lastModified = document.createElement('meta');
      lastModified.setAttribute('name', 'last-modified');
      document.head.appendChild(lastModified);
    }
    lastModified.setAttribute('content', new Date().toISOString().split('T')[0]);

    // Breadcrumb schema
    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: window.location.origin + '/'
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Open Cell Spray Foam',
          item: window.location.origin + '/open-cell-spray-foam-yuma'
        }
      ]
    };

    let breadcrumbScript = document.querySelector('script[data-schema="breadcrumb-open-cell"]');
    if (!breadcrumbScript) {
      breadcrumbScript = document.createElement('script');
      breadcrumbScript.setAttribute('type', 'application/ld+json');
      breadcrumbScript.setAttribute('data-schema', 'breadcrumb-open-cell');
      document.head.appendChild(breadcrumbScript);
    }
    breadcrumbScript.textContent = JSON.stringify(breadcrumbSchema);

    // Product schema
    const productSchema = {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: 'Open Cell Spray Foam Insulation',
      description: 'Affordable open cell spray foam insulation with R-3.5 per inch, excellent sound dampening, and flexible application for residential and commercial properties.',
      brand: {
        '@type': 'Brand',
        name: 'Yuma Spray Foam Pros'
      },
      offers: {
        '@type': 'Offer',
        availability: 'https://schema.org/InStock',
        priceCurrency: 'USD',
        priceSpecification: {
          '@type': 'PriceSpecification',
          minPrice: '1.00',
          maxPrice: '1.50',
          priceCurrency: 'USD',
          unitText: 'per board foot'
        }
      },
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: '5',
        reviewCount: '76'
      }
    };

    let productScript = document.querySelector('script[data-schema="product-open-cell"]');
    if (!productScript) {
      productScript = document.createElement('script');
      productScript.setAttribute('type', 'application/ld+json');
      productScript.setAttribute('data-schema', 'product-open-cell');
      document.head.appendChild(productScript);
    }
    productScript.textContent = JSON.stringify(productSchema);

    // LocalBusiness schema
    const localBusinessSchema = {
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      name: 'Yuma Spray Foam Pros',
      telephone: '(928) 427-8140',
      geo: {
        '@type': 'GeoCoordinates',
        latitude: 32.6927,
        longitude: -114.6277
      },
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: '5',
        reviewCount: '127'
      }
    };

    let businessScript = document.querySelector('script[data-schema="business-open-cell"]');
    if (!businessScript) {
      businessScript = document.createElement('script');
      businessScript.setAttribute('type', 'application/ld+json');
      businessScript.setAttribute('data-schema', 'business-open-cell');
      document.head.appendChild(businessScript);
    }
    businessScript.textContent = JSON.stringify(localBusinessSchema);
  }, []);

  const benefits = [
    {
      icon: 'ri-money-dollar-circle-line',
      title: 'Cost-Effective Solution',
      description: 'More affordable than closed-cell options while delivering excellent thermal performance for interior applications.'
    },
    {
      icon: 'ri-volume-mute-line',
      title: 'Superior Sound Dampening',
      description: 'Exceptional acoustic properties reduce noise transmission between rooms, creating quieter living and working spaces.'
    },
    {
      icon: 'ri-leaf-line',
      title: 'Eco-Friendly Material',
      description: 'Made with renewable resources and water-based formulas, offering a more environmentally conscious choice.'
    },
    {
      icon: 'ri-arrow-left-right-line',
      title: 'Excellent Expansion',
      description: 'Expands up to 100 times its liquid volume, filling every crack and crevice for complete coverage.'
    },
    {
      icon: 'ri-drop-line',
      title: 'Breathable Barrier',
      description: 'Allows moisture vapor to pass through, preventing trapped moisture and potential mold issues in certain applications.'
    },
    {
      icon: 'ri-flashlight-line',
      title: 'Lightweight Application',
      description: 'Lower density means less weight on structures, ideal for ceilings and walls without adding structural load.'
    }
  ];

  const applications = [
    {
      title: 'Interior Walls',
      description: 'Perfect for soundproofing between rooms while providing thermal protection.',
      image: 'https://readdy.ai/api/search-image?query=interior%20residential%20wall%20cavity%20with%20open%20cell%20spray%20foam%20insulation%20between%20wooden%20studs%2C%20soft%20white%20texture%20filling%20spaces%20completely%2C%20home%20construction%20detail%20showing%20professional%20application%20quality&width=800&height=500&seq=open-app-walls-001&orientation=landscape'
    },
    {
      title: 'Attic Spaces',
      description: 'Excellent for vented attics where breathability is important for moisture management.',
      image: 'https://readdy.ai/api/search-image?query=residential%20attic%20interior%20with%20open%20cell%20spray%20foam%20on%20roof%20deck%20and%20rafters%2C%20bright%20clean%20space%20with%20natural%20light%2C%20professional%20white%20foam%20application%20showing%20complete%20coverage&width=800&height=500&seq=open-app-attic-002&orientation=landscape'
    },
    {
      title: 'Ceiling Applications',
      description: 'Lightweight solution for ceiling insulation without adding excessive weight to joists.',
      image: 'https://readdy.ai/api/search-image?query=home%20ceiling%20interior%20with%20open%20cell%20spray%20foam%20between%20floor%20joists%2C%20clean%20white%20application%20showing%20professional%20installation%2C%20residential%20construction%20photography&width=800&height=500&seq=open-app-ceiling-003&orientation=landscape'
    },
    {
      title: 'Studio & Media Rooms',
      description: 'Superior acoustic properties make it ideal for recording studios and home theaters.',
      image: 'https://readdy.ai/api/search-image?query=modern%20home%20theater%20room%20with%20open%20cell%20spray%20foam%20on%20walls%20for%20soundproofing%2C%20professional%20acoustic%20treatment%2C%20comfortable%20entertainment%20space&width=800&height=500&seq=open-app-studio-004&orientation=landscape'
    }
  ];

  const faqs = [
    {
      question: 'What R-value does open-cell foam provide?',
      answer: 'Open-cell foam delivers an R-value of approximately 3.5 to 4 per inch. While lower than closed-cell options, it provides excellent thermal performance for interior applications at a more economical price point.'
    },
    {
      question: 'Is open-cell foam suitable for exterior walls?',
      answer: 'Open-cell foam works best for interior walls and vented attics. For exterior walls or areas requiring moisture barriers, we typically recommend closed-cell foam for superior protection against the elements.'
    },
    {
      question: 'How does it compare to closed-cell foam?',
      answer: 'Open-cell is more affordable, offers better sound dampening, and is more flexible. Closed-cell provides higher R-value, moisture barrier properties, and structural strength. We help you choose based on your specific needs.'
    },
    {
      question: 'Does it help with soundproofing?',
      answer: 'Absolutely! Open-cell foam excels at sound dampening due to its softer, more porous structure. It\'s an excellent choice for reducing noise between rooms, floors, and from outside sources.'
    },
    {
      question: 'How long does installation take?',
      answer: 'Most residential projects are completed in 1-2 days. The foam cures quickly, typically within 24 hours, allowing you to proceed with other construction or renovation work promptly.'
    },
    {
      question: 'Is it safe for my home?',
      answer: 'Yes! Once cured, open-cell foam is completely safe and non-toxic. It improves indoor air quality by sealing air leaks that allow dust, pollen, and pollutants to enter your home.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <Breadcrumb
        items={[
          { label: 'Home', href: '/' },
          { label: 'Open Cell Foam' },
        ]}
      />

      {/* Hero Section */}
      <section className="relative pt-24 pb-16 bg-gradient-to-br from-slate-50 to-white">
        <div className="max-w-4xl mx-auto text-center px-4">
          <div className="inline-block bg-blue-100 text-blue-900 px-4 py-2 rounded-full text-sm font-semibold mb-6">
            AFFORDABLE & EFFECTIVE
          </div>
          <h1 className="text-5xl font-bold text-gray-900 mb-4">Open Cell Foam Yuma AZ</h1>
          <p className="text-xl text-gray-600 leading-relaxed">
            Affordable <a href="/insulation-services-arizona" className="text-blue-900 hover:text-blue-700 font-semibold underline">open‑cell spray foam insulation</a> delivering <strong>sound dampening</strong> and <strong>thermal protection</strong> for interior applications throughout Arizona's desert region.
          </p>
          <div className="flex flex-wrap justify-center gap-4 mt-8">
            <a
              href="tel:9284278140"
              className="inline-block bg-blue-900 text-white px-8 py-4 rounded-lg font-semibold hover:bg-blue-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-line mr-2"></i>
              Call (928) 427‑8140
            </a>
            <a
              href="/insulation-services-arizona"
              className="inline-block bg-gray-900 text-white px-8 py-4 rounded-lg font-semibold hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              View All Services
            </a>
          </div>
        </div>
      </section>

      {/* Quick Stats */}
      <section className="py-12 bg-white border-y border-gray-200">
        <div className="max-w-6xl mx-auto px-4">
          <div 
            ref={statsReveal.ref}
            className={`grid grid-cols-2 md:grid-cols-4 gap-8 transition-all duration-1000 ${
              statsReveal.isVisible 
                ? 'opacity-100 translate-y-0' 
                : 'opacity-0 translate-y-8'
            }`}
          >
            <div className="text-center transform hover:scale-110 transition-transform duration-300">
              <div className="text-4xl font-bold text-blue-900 mb-2">R-3.5-4</div>
              <div className="text-sm text-gray-600">Per Inch R-Value</div>
            </div>
            <div className="text-center transform hover:scale-110 transition-transform duration-300">
              <div className="text-4xl font-bold text-blue-900 mb-2">100x</div>
              <div className="text-sm text-gray-600">Expansion Rate</div>
            </div>
            <div className="text-center transform hover:scale-110 transition-transform duration-300">
              <div className="text-4xl font-bold text-blue-900 mb-2">0.5 lb</div>
              <div className="text-sm text-gray-600">Density Per Cubic Ft</div>
            </div>
            <div className="text-center transform hover:scale-110 transition-transform duration-300">
              <div className="text-4xl font-bold text-emerald-600 mb-2">24hrs</div>
              <div className="text-sm text-gray-600">Cure Time</div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content with Tabs */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Everything You Need to Know
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Explore the benefits and technical details of open-cell foam solutions.
            </p>
          </div>

          {/* Tab Navigation */}
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            <button
              onClick={() => setActiveTab('benefits')}
              className={`px-6 py-3 rounded-lg font-semibold transition-all whitespace-nowrap cursor-pointer ${
                activeTab === 'benefits'
                  ? 'bg-blue-900 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-100'
              }`}
            >
              <i className="ri-star-line mr-2"></i>
              Benefits
            </button>
            <button
              onClick={() => setActiveTab('technical')}
              className={`px-6 py-3 rounded-lg font-semibold transition-all whitespace-nowrap cursor-pointer ${
                activeTab === 'technical'
                  ? 'bg-blue-900 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-100'
              }`}
            >
              <i className="ri-file-list-3-line mr-2"></i>
              Technical Details
            </button>
          </div>

          {/* Benefits Tab */}
          {activeTab === 'benefits' && (
            <>
              {/* Mobile: Horizontal Scroll */}
              <div className="md:hidden relative">
                <div className="overflow-x-auto pb-4 -mx-4 px-4 scrollbar-hide">
                  <div className="flex gap-6" style={{ width: 'max-content' }}>
                    {benefits.map((benefit, index) => (
                      <div key={index} className="bg-white p-8 rounded-xl shadow-md hover:shadow-lg transition-shadow" style={{ width: '300px', flexShrink: 0 }}>
                        <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center mb-6">
                          <i className={`${benefit.icon} text-2xl text-blue-900`}></i>
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-3">{benefit.title}</h3>
                        <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="text-center text-sm text-gray-500 mt-2">
                  Swipe to see more →
                </div>
              </div>

              {/* Desktop: Grid */}
              <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {benefits.map((benefit, index) => (
                  <div key={index} className="bg-white p-8 rounded-xl shadow-md hover:shadow-lg transition-shadow">
                    <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center mb-6">
                      <i className={`${benefit.icon} text-2xl text-blue-900`}></i>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{benefit.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Technical Details Tab */}
          {activeTab === 'technical' && (
            <div className="bg-white rounded-xl shadow-md p-8 md:p-12">
              <div className="grid md:grid-cols-2 gap-12">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">Performance Specifications</h3>
                  <div className="space-y-4">
                    <div className="flex justify-between py-3 border-b border-gray-200">
                      <span className="font-semibold text-gray-700">R-Value per Inch:</span>
                      <span className="text-gray-900">R-3.5 to R-4</span>
                    </div>
                    <div className="flex justify-between py-3 border-b border-gray-200">
                      <span className="font-semibold text-gray-700">Density:</span>
                      <span className="text-gray-900">0.4-0.5 lb/ft³</span>
                    </div>
                    <div className="flex justify-between py-3 border-b border-gray-200">
                      <span className="font-semibold text-gray-700">Expansion Rate:</span>
                      <span className="text-gray-900">Up to 100:1</span>
                    </div>
                    <div className="flex justify-between py-3 border-b border-gray-200">
                      <span className="font-semibold text-gray-700">Cure Time:</span>
                      <span className="text-gray-900">24 hours</span>
                    </div>
                    <div className="flex justify-between py-3 border-b border-gray-200">
                      <span className="font-semibold text-gray-700">Air Permeability:</span>
                      <span className="text-gray-900">Semi-permeable</span>
                    </div>
                    <div className="flex justify-between py-3 border-b border-gray-200">
                      <span className="font-semibold text-gray-700">Sound Absorption:</span>
                      <span className="text-gray-900">Excellent (NRC 0.70)</span>
                    </div>
                  </div>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">Key Characteristics</h3>
                  <ul className="space-y-4">
                    <li className="flex items-start">
                      <i className="ri-check-line text-emerald-600 text-xl mr-3 mt-1"></i>
                      <div>
                        <strong className="text-gray-900">Flexible Structure:</strong>
                        <p className="text-gray-600 mt-1">Soft, spongy texture allows for natural building movement without cracking.</p>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-emerald-600 text-xl mr-3 mt-1"></i>
                      <div>
                        <strong className="text-gray-900">Vapor Permeable:</strong>
                        <p className="text-gray-600 mt-1">Allows moisture vapor to pass through, preventing trapped condensation.</p>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-emerald-600 text-xl mr-3 mt-1"></i>
                      <div>
                        <strong className="text-gray-900">Water-Based Formula:</strong>
                        <p className="text-gray-600 mt-1">More environmentally friendly with lower VOC emissions.</p>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-emerald-600 text-xl mr-3 mt-1"></i>
                      <div>
                        <strong className="text-gray-900">Acoustic Performance:</strong>
                        <p className="text-gray-600 mt-1">Superior sound dampening compared to traditional materials.</p>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-check-line text-emerald-600 text-xl mr-3 mt-1"></i>
                      <div>
                        <strong className="text-gray-900">Lightweight:</strong>
                        <p className="text-gray-600 mt-1">Minimal structural load, ideal for ceiling applications.</p>
                      </div>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Comparison Section */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Open Cell vs Closed Cell
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Understanding the differences helps you choose the right solution for your project. Ideal for <a href="/residential-insulation-yuma" className="text-blue-900 hover:text-blue-700 font-semibold underline">residential homes</a> seeking cost-effective solutions.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-blue-50 p-8 rounded-xl border-2 border-blue-200">
              <div className="text-center mb-6">
                <div className="inline-block bg-blue-900 text-white px-4 py-2 rounded-full text-sm font-semibold mb-4">
                  YOU ARE HERE
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Open Cell Foam</h3>
              </div>
              <ul className="space-y-3">
                <li className="flex items-start text-gray-700">
                  <i className="ri-check-line text-blue-900 text-xl mr-3 mt-0.5"></i>
                  <span>R-3.5 to R-4 per inch</span>
                </li>
                <li className="flex items-start text-gray-700">
                  <i className="ri-check-line text-emerald-600 text-xl mr-3 mt-0.5"></i>
                  <span>More affordable option</span>
                </li>
                <li className="flex items-start text-gray-700">
                  <i className="ri-check-line text-blue-900 text-xl mr-3 mt-0.5"></i>
                  <span>Superior sound dampening</span>
                </li>
                <li className="flex items-start text-gray-700">
                  <i className="ri-check-line text-emerald-600 text-xl mr-3 mt-0.5"></i>
                  <span>Vapor permeable (breathable)</span>
                </li>
                <li className="flex items-start text-gray-700">
                  <i className="ri-check-line text-blue-900 text-xl mr-3 mt-0.5"></i>
                  <span>Lightweight application</span>
                </li>
                <li className="flex items-start text-gray-700">
                  <i className="ri-check-line text-blue-900 text-xl mr-3 mt-0.5"></i>
                  <span>Best for interior walls & attics</span>
                </li>
              </ul>
            </div>

            <div className="bg-gray-50 p-8 rounded-xl border-2 border-gray-200">
              <div className="text-center mb-6">
                <h3 className="text-2xl font-bold text-gray-900">Closed Cell Foam</h3>
              </div>
              <ul className="space-y-3">
                <li className="flex items-start text-gray-700">
                  <i className="ri-check-line text-gray-600 text-xl mr-3 mt-0.5"></i>
                  <span>R-6 to R-7 per inch</span>
                </li>
                <li className="flex items-start text-gray-700">
                  <i className="ri-check-line text-gray-600 text-xl mr-3 mt-0.5"></i>
                  <span>Premium performance</span>
                </li>
                <li className="flex items-start text-gray-700">
                  <i className="ri-check-line text-gray-600 text-xl mr-3 mt-0.5"></i>
                  <span>Moisture barrier protection</span>
                </li>
                <li className="flex items-start text-gray-700">
                  <i className="ri-check-line text-gray-600 text-xl mr-3 mt-0.5"></i>
                  <span>Vapor impermeable (sealed)</span>
                </li>
                <li className="flex items-start text-gray-700">
                  <i className="ri-check-line text-gray-600 text-xl mr-3 mt-0.5"></i>
                  <span>Adds structural strength</span>
                </li>
                <li className="flex items-start text-gray-700">
                  <i className="ri-check-line text-gray-600 text-xl mr-3 mt-0.5"></i>
                  <span>Best for exterior walls & roofs</span>
                </li>
              </ul>
              <div className="mt-6">
                <a
                  href="/closed-cell-spray-foam"
                  className="inline-block w-full text-center bg-gray-900 text-white px-6 py-3 rounded-lg font-semibold hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Learn About Closed Cell
                </a>
              </div>
            </div>
          </div>
          
          <div className="mt-12 text-center">
            <p className="text-gray-600 max-w-3xl mx-auto">
              Compare with our closed-cell premium option or explore our complete service offerings. Still unsure? Contact our <a href="/about-yuma-insulation" className="text-blue-900 hover:text-blue-700 font-semibold underline">experienced team</a> to see both options in action.
            </p>
          </div>
        </div>
      </section>

      {/* Related Services Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Explore Related Services
            </h2>
            <p className="text-lg text-gray-600">
              Discover our complete range of insulation solutions
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <a 
              href="/yuma-projects" 
              className="block bg-white p-6 rounded-xl hover:bg-blue-50 transition-colors cursor-pointer group"
            >
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4 group-hover:bg-blue-900 transition-colors">
                <i className="ri-gallery-line text-2xl text-blue-900 group-hover:text-white"></i>
              </div>
              <h4 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-blue-900">
                Completed Projects
              </h4>
              <p className="text-gray-600 text-sm">
                View our portfolio
              </p>
            </a>
            <a 
              href="/insulation-faq" 
              className="block bg-white p-6 rounded-xl hover:bg-blue-50 transition-colors cursor-pointer group"
            >
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4 group-hover:bg-blue-900 transition-colors">
                <i className="ri-question-line text-2xl text-blue-900 group-hover:text-white"></i>
              </div>
              <h4 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-blue-900">
                Common Questions
              </h4>
              <p className="text-gray-600 text-sm">
                Get instant answers
              </p>
            </a>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-blue-900 to-blue-800">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
            Ready to Improve Your Comfort?
          </h2>
          <p className="text-xl text-blue-100 mb-8 leading-relaxed">
            Get a free consultation and estimate for your project. Our experts will help you determine if open-cell foam is the right choice for your <strong>energy efficiency</strong> needs.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="tel:9284278140"
              className="inline-block bg-white text-blue-900 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-line mr-2"></i>
              Call (928) 427‑8140
            </a>
            <a
              href="/services"
              className="inline-block bg-blue-800 text-white px-8 py-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer border-2 border-white/20"
            >
              View All Services
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}